class ComponentProductForm extends Theme.Component {
  static #sections = new Map();
  static #images = new Map();

  #request = 0;
  #rendering = false;
  #submitting = false;
  #submitUnavailable = false;

  connectedCallback() {
    super.connectedCallback();
    this.#submitUnavailable = this.elements.submit.disabled;

    this.intersectionObserver((entry, observer) => {
      if (entry.isIntersecting) {
        observer.disconnect();
        this.prefetch();
      }
    });

    this.addEvent("pointerover", this.elements.form, (event) => this.preloadIntent(event.target));
    this.addEvent("focusin", this.elements.form, (event) => this.preloadIntent(event.target));

    this.addEvent("change", this.elements.form, (event) => {
      if (event.target.name?.startsWith("option_values[")) {
        this.render();
      }
    });

    this.addEvent("submit", this.elements.form, (event) => {
      event.preventDefault();
      this.submit();
    });
  }

  get optionValues() {
    return Array.from(new FormData(this.elements.form))
      .filter(([name]) => name.startsWith("option_values["))
      .map(([, value]) => value);
  }

  sectionURL(option_values) {
    const url = new URL(window.location.href);
    const section_ids = Array.from(document.querySelectorAll("[data-variant-section]"))
      .map((section) => section.dataset.variantSection);

    url.searchParams.delete("variant");
    url.searchParams.set("option_values", option_values.join(","));
    url.searchParams.set("sections", section_ids.join(","));

    return url;
  }

  load(option_values) {
    const url = this.sectionURL(option_values);

    if (!ComponentProductForm.#sections.has(url.href)) {
      const request = fetch(url)
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`Product options request returned HTTP ${response.status} ${response.statusText}.`);
          }

          try {
            return await response.json();
          } catch (cause) {
            throw new Error("Product options response was not valid JSON.", { cause });
          }
        })
        .catch((error) => {
          ComponentProductForm.#sections.delete(url.href);
          throw new Error(`Unable to load product options from ${url.href}: ${error.message}`, { cause: error });
        });

      ComponentProductForm.#sections.set(url.href, request);
    }

    return ComponentProductForm.#sections.get(url.href);
  }

  prefetch() {
    const controls = Array.from(this.elements.form.querySelectorAll("[data-option-value-id]"));
    const options = new Map();

    controls.forEach((control) => {
      const name = control.dataset.optionName;

      if (!options.has(name)) {
        options.set(name, []);
      }

      options.get(name).push(control.dataset.optionValueId);
    });

    const combinations = Array.from(options.values()).reduce(
      (combinations, values) => combinations.flatMap((combination) => values.map((value) => [...combination, value])),
      [[]]
    );

    combinations.forEach((values) => this.load(values).catch(() => {}));
  }

  preloadIntent(target) {
    const controls = target.closest("[data-option-value-id]")
      ? [target.closest("[data-option-value-id]")]
      : Array.from(target.querySelectorAll("[data-option-value-id]"));

    if (!controls.length) {
      return;
    }

    const names = [...new Set(Array.from(this.elements.form.querySelectorAll("[name^='option_values[']"), (input) => input.name))];

    controls.forEach((control) => {
      const index = names.indexOf(control.dataset.optionName);

      if (index < 0) {
        return;
      }

      const values = this.optionValues;
      values[index] = control.dataset.optionValueId;
      this.preloadImages(values);
    });
  }

  preloadImages(option_values) {
    this.load(option_values).then((html) => {
      const gallery_id = document.querySelector("component-product-gallery")?.dataset.variantSection;

      if (!gallery_id || !html[gallery_id]) {
        return;
      }

      const gallery = new DOMParser().parseFromString(html[gallery_id], "text/html");

      gallery.querySelectorAll("component-product-gallery img").forEach((image) => {
        if (ComponentProductForm.#images.has(image.src)) {
          return;
        }

        const preload = new Image();
        preload.sizes = image.sizes;
        preload.srcset = image.srcset;
        preload.src = image.src;
        ComponentProductForm.#images.set(image.src, preload);
      });
    }).catch(() => {});
  }

  async render() {
    const request = ++this.#request;
    const { form, error } = this.elements;
    const option_values = this.optionValues;
    const sections = Array.from(document.querySelectorAll("[data-variant-section]"));
    const section_ids = sections.map((section) => section.dataset.variantSection);
    const url = new URL(window.location.href);
    let stage = "loading product sections";

    url.searchParams.delete("variant");
    url.searchParams.set("option_values", option_values.join(","));

    this.#rendering = true;
    error.textContent = "";

    try {
      this.preloadImages(option_values);
      const html = await this.load(option_values);

      if (request !== this.#request || !this.isConnected) {
        return;
      }

      const parser = new DOMParser();
      const replacements = section_ids.map((id) => {
        stage = `parsing section ${id}`;

        if (typeof html?.[id] !== "string" || !html[id]) {
          throw new Error(`Section ${id} was missing, null, or empty in the product options response.`);
        }

        const replacement = parser.parseFromString(html[id], "text/html")
          .getElementById(`shopify-section-${id}`);

        if (!replacement) {
          throw new Error(`Section ${id} did not contain the expected wrapper #shopify-section-${id}.`);
        }

        document.adoptNode(replacement);

        return replacement;
      });

      const images = replacements.flatMap((section) => Array.from(
        section.querySelectorAll("component-product-gallery img, .product-form-color-image")
      ));

      stage = "decoding product images";

      await Promise.all(images.map(async (image) => {
        try {
          image.loading = "eager";
          await image.decode();
        } catch (cause) {
          throw new Error(`Unable to decode product image ${image.currentSrc || image.src}.`, { cause });
        }
      }));

      if (request !== this.#request || !this.isConnected) {
        return;
      }

      stage = "reading the selected variant";

      const focused_id = this.contains(document.activeElement) ? document.activeElement.id : null;
      const variant_id = replacements
        .map((section) => section.querySelector('input[name="id"]')?.value)
        .find(Boolean);

      url.searchParams.delete("option_values");

      if (variant_id) {
        url.searchParams.set("variant", variant_id);
      }

      sections.forEach((section, index) => {
        stage = `replacing section ${section_ids[index]}`;
        section.closest(".shopify-section").replaceWith(replacements[index]);
      });

      stage = "updating the product URL";
      history.replaceState(null, "", url);

      if (focused_id) {
        stage = "restoring focus";
        document.getElementById(focused_id)?.focus({ preventScroll: true });
      }
    } catch (failure) {
      console.log(`Product options update failed while ${stage}.`, {
        url: window.location.href,
        option_values,
        section_ids,
        failure
      });

      if (request === this.#request && this.isConnected) {
        form.reset();

        // Temporary diagnostics for reproducing product option failures.
        const messages = [`Product options update failed while ${stage}.`];

        for (let cause = failure; cause; cause = cause.cause) {
          messages.push(cause.message || String(cause));
        }

        error.textContent = messages.join(" ");
      }
    } finally {
      if (request === this.#request) {
        this.#rendering = false;
      }
    }
  }

  async submit() {
    const { form, submit, error } = this.elements;

    if (this.#submitting || this.#rendering || submit.disabled) {
      return;
    }

    this.#submitting = true;
    submit.disabled = true;
    submit.classList.add("state--loading");
    error.textContent = "";

    try {
      const response = await fetch(`${form.action}.js`, {
        method: "POST",
        body: new FormData(form)
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || "Unable to add this product to your cart.");
      }

      document.dispatchEvent(new CustomEvent("addtocart", { detail: data }));
    } catch (failure) {
      error.textContent = failure.message;
    } finally {
      this.#submitting = false;
      submit.disabled = this.#submitUnavailable;
      submit.classList.remove("state--loading");
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#request++;
  }
}

customElements.define("component-product-form", ComponentProductForm);
